import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';

import { useAuth } from '../../auth/AuthProvider';
import { useConnectionStatus } from '../../hooks/useLive';
import { useResource } from '../../hooks/useResource';
import { visibleSections } from './navigation';
import { Icon } from '../ui/Icon';
import { Button } from '../ui/Button';
import { StatusDot } from '../ui/Badge';
import { roleMeta } from '../../lib/domain';
import { initials } from '../../lib/format';
import { useTheme } from '../../hooks/useTheme';

/**
 * The frame every signed-in screen renders inside: brand rail on the left,
 * context bar across the top, page below.
 *
 * The sidebar is built from the navigation model filtered by the caller's
 * permissions, so nobody is shown a door they cannot open.
 */
export function AppShell() {
  const { user, scope, signOut, can } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const sections = visibleSections(can);

  // The open-alert count comes from the analytics summary, which counts on the
  // server. Counting the rows of a paged list would report the page size, not
  // the total — the badge would say "1" while the dashboard said "3".
  const summary = useResource(
    can('analytics:read') ? '/api/analytics/fleet' : null,
    null,
    { pollMs: 30_000 }
  );
  const openAlerts = summary.data?.alerts?.open ?? 0;

  useEffect(() => setMobileOpen(false), [location.pathname]);

  return (
    <div className="flex h-screen overflow-hidden bg-plane">
      {/* backdrop for the mobile drawer */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside
        className={[
          'fixed inset-y-0 left-0 z-40 flex w-60 flex-col border-r border-line bg-surface',
          'transition-transform lg:static lg:translate-x-0',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
        ].join(' ')}
      >
        <BrandMark />

        <nav className="flex-1 overflow-y-auto px-3 py-3">
          {sections.map((section) => (
            <div key={section.id} className="mb-5 last:mb-0">
              <p className="px-2 pb-1.5 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-ink-3">
                {section.label}
              </p>
              <ul className="space-y-0.5">
                {section.items.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.end}
                      className={({ isActive }) =>
                        [
                          'group relative flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-[13.5px] font-medium transition-colors',
                          isActive
                            ? 'bg-brand-soft text-brand-ink'
                            : 'text-ink-2 hover:bg-surface-2 hover:text-ink',
                        ].join(' ')
                      }
                    >
                      {({ isActive }) => (
                        <>
                          {/* The current item is marked in the accent hue — the
                              one place on every screen where the second brand
                              colour says "you are here". */}
                          {isActive && (
                            <span
                              className="absolute left-0 top-1/2 h-4 w-[3px] -translate-y-1/2 rounded-r-full bg-accent"
                              aria-hidden="true"
                            />
                          )}
                          <Icon
                            name={item.icon}
                            size="sm"
                            className={isActive ? 'text-brand' : 'text-ink-3 group-hover:text-ink-2'}
                          />
                          <span className="flex-1 truncate">{item.label}</span>
                          {item.badge === 'alerts' && openAlerts > 0 && (
                            <span className="rounded-full bg-critical px-1.5 py-px text-[10.5px] font-semibold text-white tabular-nums">
                              {openAlerts > 99 ? '99+' : openAlerts}
                            </span>
                          )}
                        </>
                      )}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <UserCard user={user} scope={scope} onSignOut={signOut} />
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar onOpenNav={() => setMobileOpen(true)} />
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function BrandMark() {
  return (
    <div className="flex items-center gap-2.5 border-b border-line px-4 py-3.5">
      {/* The mark is the two brand hues doing one job: a vehicle on a route. */}
      <span className="relative flex h-8 w-8 items-center justify-center rounded-lg bg-brand">
        <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-surface bg-accent" />
        <Icon name="truck" size="sm" className="text-white" strokeWidth={2} />
      </span>
      <div className="min-w-0">
        <p className="truncate text-[14px] font-semibold leading-tight text-ink">Fleet</p>
        <p className="truncate text-[11px] leading-tight text-ink-3">Telematics platform</p>
      </div>
    </div>
  );
}

function UserCard({ user, scope, onSignOut }) {
  const role = roleMeta(user?.role);

  return (
    <div className="border-t border-line p-3">
      <div className="flex items-center gap-2.5">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent-soft text-[12px] font-semibold text-accent-ink">
          {initials(user?.fullName)}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-medium text-ink">{user?.fullName}</p>
          <p className="truncate text-[11.5px] text-ink-3">{role.label}</p>
        </div>
        <Button variant="ghost" size="icon" onClick={onSignOut} aria-label="Sign out" title="Sign out">
          <Icon name="logout" size="sm" />
        </Button>
      </div>
      {scope?.fleets?.length ? (
        <p className="mt-2 truncate px-0.5 text-[11.5px] text-ink-3">
          {scope.fleets.length === 1
            ? scope.fleets[0].name
            : `${scope.fleets.length} fleets`}
        </p>
      ) : null}
    </div>
  );
}

function TopBar({ onOpenNav }) {
  const connection = useConnectionStatus();
  const { theme, toggleTheme } = useTheme();

  const connectionTone = connection === 'live' ? 'good' : connection === 'connecting' ? 'warning' : 'critical';
  const connectionLabel = { live: 'Live', connecting: 'Connecting', offline: 'Reconnecting' }[connection];

  return (
    <header className="flex h-14 shrink-0 items-center gap-3 border-b border-line bg-surface px-4">
      <Button variant="ghost" size="icon" className="lg:hidden" onClick={onOpenNav} aria-label="Open navigation">
        <Icon name="dashboard" size="sm" />
      </Button>

      <div id="page-title-slot" className="min-w-0 flex-1" />

      <StatusDot tone={connectionTone} label={connectionLabel} pulse={connection === 'live'} />

      <Button
        variant="ghost"
        size="icon"
        onClick={toggleTheme}
        aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
        title={theme === 'dark' ? 'Light theme' : 'Dark theme'}
      >
        <Icon name={theme === 'dark' ? 'sun' : 'moon'} size="sm" />
      </Button>
    </header>
  );
}

/**
 * Page heading. Every screen uses this, so the vertical rhythm at the top of a
 * page is identical everywhere.
 */
export function PageHeader({ title, subtitle, actions, breadcrumb, className = '' }) {
  return (
    <div className={`flex flex-wrap items-start justify-between gap-3 ${className}`}>
      <div className="min-w-0">
        {breadcrumb}
        <h1 className="truncate text-[21px] font-semibold tracking-tight text-ink">{title}</h1>
        {subtitle && <p className="mt-0.5 text-[13.5px] text-ink-3">{subtitle}</p>}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

/** Consistent page padding and max width. */
export function Page({ children, className = '' }) {
  return <div className={`mx-auto w-full max-w-[1500px] p-4 sm:p-5 ${className}`}>{children}</div>;
}
